// src/config/constants.js
import {
  Sparkles, Clock, BarChart2, UploadCloud, Save, Gift, Zap, Mail, LogIn, UserPlus, PackageCheck, Eye, EyeOff, X, Edit3, Download, Copy, ListChecks, HelpCircle, RefreshCw, PlusCircle, ExternalLink, Users, RotateCcw, Menu, SidebarClose, Code2, Bold, Italic, Pilcrow, List as ListIcon, ShoppingCart
} from 'lucide-react';

export const ALL_RESOURCE_COMPONENTS = [
  "Worksheet/Printable", "Digital Activity/Interactive Resource", "Google Slides/Docs/Forms",
  "PowerPoint/Word", "PDF Document", "Boom Cards", "Lesson Plan", "Unit Plan", "Entire Year Curriculum",
  "Game/Center Activity", "Assessment/Quiz/Test", "Task Cards", "Anchor Chart",
  "Craftivity/Hands-on Project", "Bulletin Board Display", "Classroom Decor",
  "Organizational Tool", "Clip Art/Graphics", "Font", "Video/Audio Resource", "Software/App Integration",
  "Worksheets", "Activity Sheets", "Printables",
  "Digital Activities (Google Slides)", "Digital Activities (Boom Cards)", "Digital Activities (Other)",
  "PowerPoint Presentation", "Google Slides Presentation",
  "Lesson Plans (Detailed)", "Unit Plan Outline",
  "Interactive Notebook Components",
  "Assessments (Quizzes)", "Assessments (Tests)", "Rubrics/Grading Guides",
  "Teacher Guide/Instructions", "Answer Key(s)", "Editable Files",
  "Graphic Organizers", "Manipulatives (Printable)", "Manipulatives (Digital)"
];

export const UNIQUE_SORTED_RESOURCE_COMPONENTS = Array.from(new Set(ALL_RESOURCE_COMPONENTS)).sort();

export const GRADE_LEVELS_CONFIG = [
  { value: "Not Grade Specific", label: "Not Grade Specific" },
  { value: "Functional Skills (All Levels)", label: "Functional Skills (All Levels)" },
  { value: "Pre-K", label: "Pre-K" },
  { value: "Kindergarten", label: "Kindergarten" }, { value: "1st Grade", label: "1st Grade" },
  { value: "2nd Grade", label: "2nd Grade" }, { value: "3rd Grade", label: "3rd Grade" },
  { value: "4th Grade", label: "4th Grade" }, { value: "5th Grade", label: "5th Grade" },
  { value: "6th Grade", label: "6th Grade" }, { value: "7th Grade", label: "7th Grade" },
  { value: "8th Grade", label: "8th Grade" }, { value: "High School", label: "High School (General 9-12)" },
  { value: "9th Grade", label: "9th Grade" }, { value: "10th Grade", label: "10th Grade" },
  { value: "11th Grade", label: "11th Grade" }, { value: "12th Grade", label: "12th Grade" },
  { value: "Higher Education", label: "Higher Education" }, { value: "Adult Education", label: "Adult Education" },
  { value: "Homeschool", label: "Homeschool" }, { value: "Staff", label: "Staff" }
];

export const PEDAGOGICAL_APPROACHES = [
  "Differentiated Instruction", "Problem Solving Focus", "Inquiry-Based Learning",
  "Collaborative Learning", "Modeling Examples", "Use of Diagrams/Visuals",
  "Real-World Connections", "Technology Integration", "Hands-on Activities",
  "Project-Based Learning", "Scaffolding Techniques", "Formative Assessment Opportunities",
  "Critical Thinking Prompts", "Student Choice/Autonomy", "Interdisciplinary Connections"
];

export const SUBJECT_CONFIG = {
  // --- Arts disciplines (NCAS) ---
  "Visual Arts": {
    defaultDomains: ["Creating", "Presenting", "Responding", "Connecting"],
    sampleStandard: { code: "NAEA.VA:Cr2.1.5a", description: "Experiment and develop skills in multiple art-making techniques and approaches through practice." }
  },
  "Music": {
    defaultDomains: ["Creating", "Performing", "Responding", "Connecting"],
    sampleStandard: { code: "NAfME.MU:Pr4.2.4a", description: "Demonstrate understanding of expressive qualities and how they support the musical idea." }
  },
  "Theater": {
    defaultDomains: ["Creating", "Performing", "Responding", "Connecting"],
    sampleStandard: { code: "NCAS.TH:Cr1.1.5", description: "Create roles and devise improvised scenes based on personal experiences and imagination." }
  },
  "Dance": {
    defaultDomains: ["Creating", "Performing", "Responding", "Connecting"],
    sampleStandard: { code: "NCAS.DA:Pr4.1.4", description: "Demonstrate body patterning and technical dance skills in movement combinations." }
  },
  "Media Arts": {
    defaultDomains: ["Creating", "Producing", "Responding", "Connecting"],
    sampleStandard: { code: "NCAS.MA:Cr2.1.6", description: "Organize and develop media artworks considering the goals, audience, and context." }
  },

  // --- ELA (CCSS) ---
  "English Language Arts (ELA)": {
    defaultDomains: [
      "Reading: Literature (RL)", "Reading: Informational Text (RI)",
      "Reading: Foundational Skills (RF)", "Writing (W)",
      "Speaking & Listening (SL)", "Language (L)"
    ],
    sampleStandard: { code: "CCSS.ELA-LITERACY.RL.7.1", description: "Cite several pieces of textual evidence to support analysis of what the text says explicitly as well as inferences drawn from the text." }
  },
  "French": {
    defaultDomains: [
      "Reading",
      "Writing",
      "Speaking",
      "Listening",
      "Culture & Civilization",
      "Grammar & Vocabulary"
    ],
    sampleStandard: null
  },

  "Spanish": {
    defaultDomains: [
      "Reading",
      "Writing",
      "Speaking",
      "Listening",
      "Culture & Civilization",
      "Grammar & Vocabulary"
    ],
    sampleStandard: null
  },

  // --- Health (NHES) ---
  "Health": {
    defaultDomains: [
      "Standard 1: Core Concepts",
      "Standard 2: Analyzing Influences",
      "Standard 3: Accessing Valid Information & Services",
      "Standard 4: Interpersonal Communication",
      "Standard 5: Decision-Making",
      "Standard 6: Goal-Setting",
      "Standard 7: Health-Enhancing Behaviors",
      "Standard 8: Advocacy"
    ],
    sampleStandard: { code: "1.12.1", description: "Predict how healthy behaviors can affect health status." }
  },

  // --- Math (CCSS) ---
  "Mathematics": {
    k8Domains: [
      "Mathematical Practices", "Counting and Cardinality", "Operations and Algebraic Thinking",
      "Number and Operations in Base Ten", "Measurement and Data", "Geometry",
      "Ratios and Proportional Relationships", "The Number System", "Expressions and Equations",
      "Functions", "Statistics and Probability"
    ],
    hsDomains: [
      "Mathematical Practices", "Number and Quantity", "Algebra", "Functions",
      "Modeling", "Geometry", "Statistics and Probability"
    ],
    defaultDomains: [
      "Mathematical Practices", "Counting and Cardinality", "Operations and Algebraic Thinking",
      "Number and Operations in Base Ten", "Measurement and Data", "Geometry",
      "Ratios and Proportional Relationships", "The Number System", "Expressions and Equations",
      "Functions", "Statistics and Probability", "Number and Quantity", "Algebra", "Modeling"
    ],
    sampleStandard: { code: "CCSS.MATH.CONTENT.HSG.CO.A.1", description: "Know precise definitions of angle, circle, perpendicular line, parallel line, and line segment…" }
  },

  // --- Occupational Therapy (custom framework) ---
  "Occupational Therapy": {
    defaultDomains: [
      "Executive Functioning Skills", "Cognitive & Perceptual Skills", "Self-Advocacy & Determination",
      "Personal Health & Safety", "Home Management & Maintenance", "Financial Literacy",
      "Vocational & Career Skills", "Community & Transportation", "Social & Recreational Skills",
      "Digital Literacy & Technology"
    ],
    sampleStandard: { code: "OT.EFS.3", description: "Gather and organize all necessary materials before starting an activity." }
  },

  "Other": {
    defaultDomains: [
      "General Resource", "Classroom Management", "Professional Development",
      "Study Skills", "Seasonal/Holiday", "Cross-Curricular"
    ],
    sampleStandard: null
  },

  // --- Physical Education (SHAPE America) ---
  "Physical Education": {
    defaultDomains: [
      "Standard 1: Motor Skills & Movement Patterns",
      "Standard 2: Movement & Performance",
      "Standard 3: Physical Activity Knowledge",
      "Standard 4: Health-Enhancing Fitness",
      "Standard 5: Personal/Social Responsibility"
    ],
    sampleStandard: { code: "S4.M1.6", description: "Exhibits responsible personal and social behavior that respects self and others." }
  },

  // --- Science (NGSS) ---
  "Science": {
    defaultDomains: [
      "Physical Science",
      "Life Science",
      "Earth and Space Science",
      "Engineering, Technology, and Applications of Science"
    ],
    sampleStandard: { code: "NGSS.MS-LS1-1", description: "Conduct an investigation to provide evidence that living things are made of cells…" }
  },

  "Religion": {
    defaultDomains: [
      "Sacred Texts & Teachings",
      "Beliefs & Practices",
      "Ethics & Morality",
      "Religious History & Culture",
      "Comparative Religion",
      "Faith & Community"
    ],
    sampleStandard: null
  },


  // --- NEW: SEL (CASEL) ---
  "SEL (Social Emotional Learning)": {
    defaultDomains: [
      "Self-Awareness",
      "Self-Management",
      "Social Awareness",
      "Relationship Skills",
      "Responsible Decision-Making",
      "Mindfulness & Well-being",
      "Growth Mindset"
    ],
    sampleStandard: { code: "CASEL.2A.4", description: "Analyze how thoughts, feelings, and behaviors are interconnected." }
  },

  // --- History (C3-aligned) ---
  "History": {
    defaultDomains: [
      "Change, Continuity & Context",
      "Perspectives",
      "Historical Sources & Evidence",
      "Causation & Argumentation",
      "U.S. History",
      "World History"
    ],
    sampleStandard: { code: "C3.D2.His.1.6-8", description: "Analyze connections among events and developments in broader historical contexts." }
  },

  // --- NEW: Geography (NCGE core + NAEP + APHG) ---
  "Geography": {
    defaultDomains: [
      // Core NCGE/5 Themes-ish + skills
      "The World in Spatial Terms",
      "Places & Regions",
      "Physical Systems",
      "Human Systems",
      "Environment & Society",
      "Uses of Geography",
      "Geospatial Technologies & Skills",
      "Regional & Global Studies",
      // NAEP content areas
      "NAEP: Space & Place",
      "NAEP: Environment & Society",
      "NAEP: Spatial Dynamics & Connections",
      // AP Human Geography units
      "APHG: Unit 1 – Thinking Geographically",
      "APHG: Unit 2 – Population & Migration",
      "APHG: Unit 3 – Cultural Patterns & Processes",
      "APHG: Unit 4 – Political Patterns",
      "APHG: Unit 5 – Agriculture, Food Production & Rural Land Use",
      "APHG: Unit 6 – Cities & Urban Land Use",
      "APHG: Unit 7 – Industrial & Economic Development"
    ],
    sampleStandard: {
      code: "GEO.6-8.Spatial.1",
      description: "Use maps and geospatial tools to analyze the spatial organization of people, places, and environments."
    }
  },

  "Special Education": {
    defaultDomains: ["Life Skills", "IEP Resources", "Behavior Management", "Adapted Curriculum", "Social Skills", "Fine Motor Skills"],
    sampleStandard: null
  },

  // --- NEW: Speech & Language ---
  "Speech & Language": {
    defaultDomains: [
      "Articulation & Phonology",
      "Receptive Language (Listening & Comprehension)",
      "Expressive Language (Semantics & Syntax)",
      "Pragmatics / Social Communication",
      "Narrative & Discourse",
      "Morphology & Word Study",
      "Fluency (Stuttering) & Self-Advocacy",
      "Voice & Resonance",
      "Hearing & Auditory (Listening/Processing)",
      "AAC Competence"
    ],
    sampleStandard: {
      code: "SLP.K-2.ART.1",
      description: "Produce age-expected consonants and vowels in words and simple sentences during structured tasks."
    }
  },

  // --- Technology umbrella (for users who prefer a single subject) ---
  "Technology": {
    defaultDomains: ["Computer Science", "Digital Literacy", "Coding", "Keyboarding", "STEM", "Digital Citizenship"],
    sampleStandard: null
  },

  // --- Standalone tech-related subjects ---
  "Computer Science": {
    defaultDomains: [
      "Computing Systems",
      "Networks & the Internet",
      "Data & Analysis",
      "Algorithms & Programming",
      "Impacts of Computing"
    ],
    sampleStandard: { code: "CSTA 2-AP-13", description: "Decompose problems and create procedures to solve them." }
  },

  "Coding": {
    defaultDomains: [
      "Algorithms & Problem Solving",
      "Programming Fundamentals",
      "Control Structures",
      "Data Structures",
      "Testing & Debugging",
      "Project Practices"
    ],
    sampleStandard: { code: "CD.6-8.PF.1", description: "Use variables and expressions to represent values and computations in programs." }
  },

  "Digital Citizenship": {
    defaultDomains: [
      "Media Balance & Well-Being",
      "Privacy & Security",
      "Digital Footprint & Identity",
      "Relationships & Communication",
      "Cyberbullying, Digital Drama & Hate Speech",
      "News & Media Literacy",
      "Creative Credit & Copyright"
    ],
    sampleStandard: { code: "DC.6-8.Privacy.1", description: "Explain how personal data is collected, used, and shared and how to protect privacy." }
  },

  "Digital Literacy": {
    defaultDomains: [
      "Information & Media Literacy",
      "Communication & Collaboration",
      "Research & Information Fluency",
      "Critical Thinking & Problem Solving",
      "Digital Tools & Operations",
      "Safety & Ethics"
    ],
    sampleStandard: { code: "DL.6-8.Info.1", description: "Locate, evaluate, and select credible digital information for a specific task." }
  },

  "ISTE": {
    defaultDomains: [
      "1. Empowered Learner",
      "2. Digital Citizen",
      "3. Knowledge Constructor",
      "4. Innovative Designer",
      "5. Computational Thinker",
      "6. Creative Communicator",
      "7. Global Collaborator"
    ],
    sampleStandard: { code: "ISTE 1a", description: "Articulate and set personal learning goals, leverage technology to achieve them, and reflect on the process." }
  },

  "World Languages": {
    defaultDomains: ["Spanish", "French", "German", "Mandarin", "Latin", "ASL", "Culture"],
    sampleStandard: null
  },

  // --- ESL / ELL ---
  "ESL / ELL": {
    defaultDomains: [
      "Vocabulary & Word Study",
      "Reading Comprehension",
      "Writing Skills",
      "Speaking & Listening",
      "Grammar & Sentence Structure",
      "Phonics & Phonological Awareness",
      "Academic Language",
      "Newcomer Support",
      "Sheltered Instruction",
      "Content-Based Language Learning",
      "Cultural Awareness & Identity",
      "Test Prep (WIDA / ELPA21)"
    ],
    sampleStandard: {
      code: "WIDA ELD.3-5.Speaking.2",
      description: "Engage in collaborative discussions using grade-appropriate academic vocabulary and sentence frames to express ideas."
    }
  },

  // --- NEW: Business ---
  "Business": {
    defaultDomains: [
      "Accounting & Finance",
      "Marketing",
      "Management & Entrepreneurship",
      "Business Technology",
      "Economics",
      "Ethics & Law",
      "Professional Communication"
    ],
    sampleStandard: {
      code: "BUS.HS.MKT.3",
      description: "Develop a basic marketing plan that defines target audience, value proposition, and promotional channels."
    }
  },

  // --- NEW: Careers ---
  "Careers": {
    defaultDomains: [
      "Career Exploration",
      "Employability Skills",
      "Workplace Readiness",
      "Career Planning & Pathways",
      "Financial Literacy",
      "Digital Portfolio & Professional Communication",
      "Entrepreneurship"
    ],
    sampleStandard: {
      code: "CAR.6-8.EMP.2",
      description: "Demonstrate interview preparation strategies, including STAR responses and post-interview etiquette."
    }
  }
};

// ADDED THIS ARRAY TO FIX THE IMPORT ERROR
export const SEASONAL_THEMES = [
  "Back to School", "Fall/Autumn", "Halloween", "Thanksgiving", "Winter", "Christmas", "Holidays Around the World",
  "New Year's", "Martin Luther King Jr. Day", "Groundhog Day", "Valentine's Day", "Presidents' Day",
  "100th Day of School", "Read Across America", "St. Patrick's Day", "Spring", "Easter", "Earth Day",
  "Mother's Day", "Memorial Day", "End of Year", "Summer", "Father's Day", "4th of July/Independence Day"
];

export const DESCRIPTION_TONES = [
  "Engaging and Professional", "Fun and Playful", "Warm and Friendly",
  "Direct and Informative", "Enthusiastic and Passionate", "Clear and Concise",
  "Creative and Imaginative", "Supportive and Encouraging"
];

export const DESCRIPTION_LENGTHS_NEW = [
  { value: "Brief (approx. 30-60 words)", label: "Brief (approx. 30-60 words)" },
  { value: "Standard (approx. 70-120 words)", label: "Standard (approx. 70-120 words)" },
  { value: "Detailed (approx. 130-200 words)", label: "Detailed (approx. 130-200 words)" },
  { value: "Comprehensive (approx. 200-300 words)", label: "Comprehensive (approx. 200-300 words)" }
];

export const DESCRIPTION_LENGTHS_REWRITE = [
  { value: "Concise (focus on key improvements)", label: "Concise (focus on key improvements)" },
  { value: "Standard (balanced rewrite)", label: "Standard (balanced rewrite)" },
  { value: "Detailed (more expansive rewrite)", label: "Detailed (more expansive rewrite)" },
  { value: "Comprehensive (full restructuring if needed)", label: "Comprehensive (full restructuring if needed)" }
];

export const MAX_TRIAL_GENERATIONS_DISPLAY = 10;

export const MAX_TRIAL_PRODUCT_SAVES_DISPLAY = 5;

export const TPT_PURCHASE_LINK = "https://www.teacherspayteachers.com/Product/TPT-Listing-Optimizer-Pro-App-Generates-Smart-Descriptions-Titles-More-13645689";

export const TPT_STORE_LINK = "https://www.teacherspayteachers.com/store/mr-joels-math-science-store";

export const SUPPORT_EMAIL = "info@mrjoelstptgenerator.com";

export const APP_URL = "https://mrjoelstptgenerator.com/";

export const SELLING_POINTS = [
  { icon: Sparkles, text: "AI-Powered Content: Descriptions & Titles that sell.", color: "text-purple-500" },
  { icon: Clock, text: "Save Hours Weekly: More creating, less writing.", color: "text-green-500" },
  { icon: BarChart2, text: "Boost Your Visibility: SEO optimized for TPT.", color: "text-blue-500" },
  { icon: UploadCloud, text: "Import & Revamp: Instantly upgrade listings by importing your descriptions direct from TPT URLs.", color: "text-orange-500" },
  { icon: Save, text: "All Your Work Saved: Access & edit anytime.", color: "text-teal-500" }
];